import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { X, Plus } from "lucide-react";
import { cardCls, inputCls, labelCls } from "./ui";

const STATUS_DOT = {
  AVAILABLE: "#10B981", ALLOCATION: "#F59E0B", SOURCING: "#0A192F", PROJECT: "#6366F1", ENQUIRE: "#6B7280",
};
const AVAILABILITY_STATUSES = ["AVAILABLE", "ALLOCATION", "SOURCING", "PROJECT", "ENQUIRE"];
const EMPTY_LISTING = { category: "", description: "", spec_class: "", quantity_range: "", region: "", lead_time: "", status: "ENQUIRE", group: "", notes: "", active: true, order: 0 };
const LISTING_GROUPS = ["", "GPU & AI", "SERVERS", "CPU", "MEMORY", "STORAGE", "NETWORKING", "COMPONENTS", "CONSUMER TECH"];

const ListingEditor = ({ listing, onClose, onSave }) => {
  const [form, setForm] = useState(listing);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  return (
    <div className="fixed inset-0 z-50 bg-midnight/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="listing-editor-modal">
      <div className="bg-white hairline shadow-medium max-w-xl w-full max-h-[85vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-heading text-lg text-midnight">{form.id ? "Edit Listing" : "New Listing"}</h3>
          <button onClick={onClose} className="text-midnight/40 hover:text-midnight" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={labelCls}>Category *</label><input value={form.category} onChange={set("category")} data-testid="listing-category" className={inputCls} /></div>
          <div>
            <label className={labelCls}>Status</label>
            <select value={form.status} onChange={set("status")} data-testid="listing-status" className={inputCls}>
              {AVAILABILITY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Board Tab (Group)</label>
            <select value={form.group || ""} onChange={set("group")} data-testid="listing-group" className={inputCls}>
              {LISTING_GROUPS.map((g) => <option key={g} value={g}>{g || "Auto (from category)"}</option>)}
            </select>
          </div>
          <div className="col-span-2"><label className={labelCls}>Description</label><input value={form.description} onChange={set("description")} data-testid="listing-description" className={inputCls} /></div>
          <div className="col-span-2"><label className={labelCls}>Specification / Class</label><input value={form.spec_class} onChange={set("spec_class")} className={inputCls} /></div>
          <div><label className={labelCls}>Quantity Range</label><input value={form.quantity_range} onChange={set("quantity_range")} className={inputCls} /></div>
          <div><label className={labelCls}>Region</label><input value={form.region} onChange={set("region")} className={inputCls} /></div>
          <div><label className={labelCls}>Lead Time</label><input value={form.lead_time} onChange={set("lead_time")} className={inputCls} /></div>
          <div><label className={labelCls}>Display Order</label><input type="number" value={form.order} onChange={set("order")} className={inputCls} /></div>
          <div className="col-span-2"><label className={labelCls}>Notes</label><input value={form.notes} onChange={set("notes")} className={inputCls} /></div>
          <label className="col-span-2 flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={set("active")} data-testid="listing-active" className="w-4 h-4 accent-[#C5A059]" />
            <span className="text-sm text-midnight/65">Active (visible on public board)</span>
          </label>
        </div>
        <button onClick={() => onSave(form)} data-testid="listing-save" className="mt-6 w-full bg-midnight text-white text-sm font-semibold py-3.5 hover:bg-midnight-light transition-colors">
          Save Listing
        </button>
      </div>
    </div>
  );
};

export const AvailabilitySection = () => {
  const [listings, setListings] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/availability").then((r) => setListings(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    if (!form.category.trim()) { toast.error("Category is required"); return; }
    const payload = { ...form, order: Number(form.order) || 0 };
    delete payload.id; delete payload.created_at;
    if (form.id) await api.put(`/admin/availability/${form.id}`, payload);
    else await api.post("/admin/availability", payload);
    setEditing(null);
    load();
    toast.success("Listing saved");
  };

  const remove = async (id) => {
    await api.delete(`/admin/availability/${id}`);
    load();
    toast.success("Listing removed");
  };

  return (
    <div data-testid="admin-availability-table">
      <button onClick={() => setEditing({ ...EMPTY_LISTING })} data-testid="availability-add" className="mb-5 flex items-center gap-2 bg-midnight text-white text-sm font-medium px-5 py-3 hover:bg-midnight-light transition-colors">
        <Plus size={14} /> New Listing
      </button>
      <div className={cardCls}>
        {listings.map((l) => (
          <div key={l.id} className="grid grid-cols-2 md:grid-cols-12 gap-3 items-center px-5 py-4 border-b border-midnight/[0.06]" data-testid={`admin-listing-${l.id}`}>
            <span className="text-sm font-medium text-midnight md:col-span-3">{l.category}</span>
            <span className="text-xs text-midnight/50 hidden md:block md:col-span-4">{l.description}</span>
            <span className="flex items-center gap-2 md:col-span-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: STATUS_DOT[l.status] }} />
              <span className="text-[11px] font-bold tracking-wide text-midnight/70">{l.status}</span>
            </span>
            <span className={`text-[11px] font-medium ${l.active ? "text-emerald-600" : "text-midnight/30"}`}>{l.active ? "Live" : "Paused"}</span>
            <span className="flex gap-4 justify-end md:col-span-2">
              <button onClick={() => setEditing(l)} data-testid={`listing-edit-${l.id}`} className="text-[11px] font-bold tracking-wide text-midnight hover:text-gold">EDIT</button>
              <button onClick={() => remove(l.id)} data-testid={`listing-delete-${l.id}`} className="text-[11px] font-bold tracking-wide text-midnight/30 hover:text-red-500">DELETE</button>
            </span>
          </div>
        ))}
      </div>
      {editing && <ListingEditor listing={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
};
